/**
 * @fileoverview Component for displaying detailed information about a staff member.
 * This component includes a header with the staff member's name and a profile block with various details.
 * It also displays the staff member's roles, including sites, studies, and roles.
 */

import { useParams } from 'react-router-dom'
import { useGetStaffByIdQuery } from '../../../services/staff'
import { StaffCardHeader } from './StaffCardHeader'

import { formatDate, formatDateToRelative } from '../../../utils/formatDate'
import { formatOutput } from '../../../utils/formatOutput'
import { ProfileBlock } from '../../ProfileBlock'
import { StaffStatusDropdown } from '../StaffStatusDropdown'

import StudyIcon from '../../../assets/filters/study.svg'
import SiteIcon from '../../../assets/site.svg'

/**
 * @function StaffCard
 * @description Component for displaying detailed information about a staff member.
 * @returns {JSX.Element} - A React component that renders the staff member's details.
 */
export function StaffCard() {
    const { userId } = useParams()
    const { data: staff, isLoading } = useGetStaffByIdQuery(userId)

    const hint = `Setting a staff account to 'Inactive' will block Dashboard access. This action does not affect the data associated with the account, and the account can be reactivated at any time.`

    if (isLoading) {
        return null
    }

    return (
        <div className="flex flex-col h-full">
            <StaffCardHeader user={staff} />

            <div className="p-8">
                <ProfileBlock
                    title="Details"
                    content={[
                        ['Name', formatOutput(staff.name)],
                        ['Email', formatOutput(staff.email)],
                        ['Invited', formatDate(staff.created_at)],
                        ['Last active', formatDateToRelative(staff.last_activity_at)],
                        [
                            'Account status',
                            <StaffStatusDropdown key={staff.id} staff={staff} />,
                            hint,
                        ],
                    ]}
                />
            </div>

            <div>
                <h1 className="font-inter text-xl font-semibold leading-7 text-left px-8 pb-4">
                    Roles
                </h1>

                <div className="flex flex-col gap-y-3">
                    {staff.roles.map(({ id, site, study, role }: Novoic.API.RoleProxy) => (
                        <div
                            key={id}
                            className="grid grid-cols-[200px,1fr] border-t pt-4 last:border-b last:pb-4"
                        >
                            <div className="w-40 pl-8 h-7 text-sm text-gray-600">Site</div>
                            <div className="flex h-7 gap-x-2 font-medium text-sm">
                                <SiteIcon className="text-indigo-500" />
                                {site?.name || 'All sites'}
                            </div>

                            <div className="w-40 pl-8 h-7 text-sm text-gray-600">Study</div>
                            <div className="flex h-7 gap-x-2 font-medium text-sm">
                                <StudyIcon className="text-indigo-500" />
                                {study?.name || 'All studies'}
                            </div>

                            <div className="w-40 pl-8 h-7 text-sm text-gray-600">Role</div>
                            <div className="h-7 font-medium text-sm">{role.name}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
